import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";

/**
 * PremiumHero — Fills the viewport under the navbar, with layered overlay:
 * Layer1 gradient (horizontal desktop / vertical mobile), Layer2 vignette,
 * Layer3 text shadow. Kinetic on-load masked reveal for H1 line-by-line.
 * On home: subtle parallax on scroll.
 */
export default function Hero({
    eyebrow,
    title, // array of lines OR string
    subtitle,
    subtitleMobile, // optional shorter version for mobile
    imgDesktop,
    imgMobile,
    primaryCta,
    secondaryCta,
    variant = "internal", // "home" | "internal"
}) {
    const ref = useRef(null);
    const { scrollY } = useScroll();
    const yImg = useTransform(scrollY, [0, 800], ["0%", "12%"]);
    const scaleImg = useTransform(scrollY, [0, 800], [1.02, 1.08]);

    const lines = Array.isArray(title) ? title : [title];

    return (
        <section
            ref={ref}
            data-testid="hero"
            className="hero-shell flex flex-col relative min-h-[100dvh] md:min-h-[calc(100dvh-100px)] mt-0 md:mt-[100px]"
        >
            <picture>
                <source media="(min-width: 768px)" srcSet={imgDesktop} />
                <motion.img
                    src={imgMobile}
                    alt=""
                    className="hero-img no-drag"
                    style={{ y: yImg, scale: scaleImg }}
                    loading="eager"
                    fetchPriority="high"
                    draggable={false}
                />
            </picture>
            <div className="hero-overlay-h" />
            <div className="hero-vignette" />

            {/* Content */}
            <div className="relative z-10 flex-1 flex items-end md:items-center">
                <div className="mx-auto max-w-[1440px] w-full px-6 md:px-12 pb-16 md:pb-0">
                    <div className="max-w-[720px] hero-text">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            className="inline-flex items-center gap-3 text-ivory/95 text-[12px] md:text-[13px] uppercase tracking-[0.28em] mb-5 md:mb-8"
                        >
                            <span className="w-8 h-px bg-rose-powder" />
                            <Sparkles size={12} className="text-rose-powder" />
                            {eyebrow}
                        </motion.div>

                        <h1
                            className={`font-display text-ivory ${
                                variant === "home"
                                    ? "text-[38px] sm:text-[52px] lg:text-[64px]"
                                    : "text-[32px] sm:text-[44px] lg:text-[54px]"
                            } leading-[1.05] tracking-tight`}
                        >
                            {lines.map((line, i) => (
                                <span key={i} className="mask-line">
                                    <motion.span
                                        initial={{ y: "110%" }}
                                        animate={{ y: 0 }}
                                        transition={{
                                            duration: 0.9,
                                            delay: 0.15 + i * 0.12,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                    >
                                        {line}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        {subtitle && (
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.9,
                                    delay: 0.35 + lines.length * 0.12,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="mt-6 md:mt-8 text-ivory/90 text-[15px] md:text-lg leading-relaxed max-w-[540px]"
                            >
                                <span className="hidden md:inline">{subtitle}</span>
                                <span className="md:hidden">{subtitleMobile || subtitle}</span>
                            </motion.p>
                        )}

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.55 + lines.length * 0.12,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="mt-8 md:mt-10 flex flex-wrap items-center gap-4"
                        >
                            <Link
                                to={
                                    primaryCta?.href || "/contatti"
                                }
                                data-testid="hero-cta-primary"
                                className="group inline-flex items-center gap-3 rounded-full bg-raspberry text-ivory pl-6 pr-2 py-2 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-raspberry-deep transition-all shadow-soft"
                            >
                                {primaryCta?.label || "Prenota un appuntamento"}
                                <span className="w-9 h-9 rounded-full bg-ivory text-raspberry grid place-items-center group-hover:rotate-45 transition-transform duration-500">
                                    <ArrowUpRight size={16} />
                                </span>
                            </Link>
                            {secondaryCta && (
                                <Link
                                    to={secondaryCta.href}
                                    data-testid="hero-cta-secondary"
                                    className="inline-flex items-center gap-2 rounded-full border border-ivory/40 bg-ivory/5 backdrop-blur text-ivory px-6 py-3.5 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-ivory/15 transition-all"
                                >
                                    {secondaryCta.label}
                                </Link>
                            )}
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Scroll hint (home only) */}
            {variant === "home" && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.6, duration: 1 }}
                    className="hidden md:flex absolute bottom-8 right-8 z-10 items-center gap-3 text-ivory/70 text-[10px] uppercase tracking-[0.32em]"
                >
                    Scroll
                    <span className="relative w-px h-10 bg-ivory/30 overflow-hidden">
                        <motion.span
                            animate={{ y: ["-100%", "100%"] }}
                            transition={{
                                duration: 1.8,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            className="absolute inset-0 bg-rose-powder"
                        />
                    </span>
                </motion.div>
            )}
        </section>
    );
}
